import Link from 'next/link';
import { z } from 'zod';

import { modelMenu } from '~/lib/api/models';
import routeGenerator from '~/lib/api/routes';
import { cn } from '~/lib/utils/classname';

export type Props = {
  item: z.infer<
    typeof modelMenu.schema
  >['menu']['menuItems']['edges'][number]['node'];

  className?: string;
};

const MenuItem: React.FC<Props> = ({ item, className }) => {
  if (item && item.connectedNode && item.connectedNode.node) {
    const { node } = item.connectedNode;

    switch (node.__typename) {
      case 'Page': {
        return (
          <Link
            href={routeGenerator.pageByUri({ uri: item.uri || '/' })}
            className={cn(
              'hocus:opacity-50 transition-all max-lg:-m-2 max-lg:p-2',
              className
            )}
          >
            {item.label || ''}
          </Link>
        );
      }

      case 'Property': {
        const category = node.propertyTypes.edges.at(0)?.node?.slug;

        if (category) {
          return (
            <Link
              href={routeGenerator.property({
                category,
                slug: node.slug,
              })}
              className={cn(
                'hocus:opacity-50 transition-all max-lg:-m-2 max-lg:p-2',
                className
              )}
            >
              {item.label || ''}
            </Link>
          );
        } else {
          return null;
        }
      }

      case 'PropertyType': {
        if (node.slug) {
          return (
            <Link
              href={routeGenerator.propertyType({
                category: item.connectedNode.node.slug,
              })}
              className={cn(
                'hocus:opacity-50 transition-all max-lg:-m-2 max-lg:p-2',
                className
              )}
            >
              {item.label || ''}
            </Link>
          );
        } else {
          return null;
        }
      }

      case 'Post': {
        if (node.slug) {
          return (
            <Link
              href={routeGenerator.post({
                category: node.categories.edges.map(
                  (category) => category.node.slug
                )[0],

                slug: item.connectedNode.node.slug,
              })}
              className={cn(
                'hocus:opacity-50 transition-all max-lg:-m-2 max-lg:p-2',
                className
              )}
            >
              {item.label || ''}
            </Link>
          );
        } else {
          return null;
        }
      }

      case 'Category': {
        if (node.slug) {
          return (
            <Link
              href={routeGenerator.postCategory({ category: node.slug })}
              className={cn(
                'hocus:opacity-50 transition-all max-lg:-m-2 max-lg:p-2',
                className
              )}
            >
              {item.label || ''}
            </Link>
          );
        } else {
          return null;
        }
      }

      default: {
        return null;
      }
    }
  }

  if (item.target) {
    return (
      <a
        className={cn(
          'hocus:opacity-50 transition-all max-lg:-m-2 max-lg:p-2',
          className
        )}
        href={item.uri || undefined}
        target={item.target}
      >
        {item.label}
      </a>
    );
  }

  return (
    <Link
      href="/"
      className={cn(
        'hocus:opacity-50 transition-all max-lg:-m-2 max-lg:p-2',
        className
      )}
    >
      {item.target}
    </Link>
  );
};

export default MenuItem;
